import { useCallback, useEffect, useRef, useState } from "react";
import { SUBCHUNK_SEC, splitSegment } from "../../convex/lib/game";
import { useClipActions } from "../components/backend";
import { Button } from "../components/Button";
import type { Auth, Clip, SegmentLength, ToastTone } from "../components/contracts";
import { Footer } from "../components/Footer";
import { NavBar, NavWord } from "../components/NavBar";
import { Panel } from "../components/Panel";
import { clamp, formatTime, errorText } from "../components/util";

const KEY_STEP = 0.5;
const KEY_STEP_LARGE = 5;

export interface SegmentPickerProps {
  auth: Auth;
  clip: Clip;
  segmentLength: SegmentLength;
  onDone: () => void;
  onToast: (text: string, tone?: ToastTone) => void;
}

/**
 * The clip's owner picks which `segmentLength` seconds get dubbed — they know
 * where the funny part is. The window is a FIXED width; you slide it, you
 * never resize it, because the segment length is the host's setting.
 */
export function SegmentPicker({
  auth,
  clip,
  segmentLength,
  onDone,
  onToast,
}: SegmentPickerProps) {
  const { setSegment } = useClipActions();
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const grabOffset = useRef(0);

  const duration = Math.max(clip.durationSec, segmentLength);
  const maxStart = Math.max(0, duration - segmentLength);
  const [start, setStart] = useState(() => clamp(clip.segmentStart, 0, maxStart));
  const [dragging, setDragging] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const chunks = splitSegment(start, segmentLength);
  const widthPct = (segmentLength / duration) * 100;
  const leftPct = (start / duration) * 100;

  /** Keep the poster frame on the first frame of the chosen segment. */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || previewing) return;
    if (Math.abs(video.currentTime - start) > 0.05) video.currentTime = start;
  }, [start, previewing]);

  /** Stop the preview dead on the segment boundary. Never mux, never encode. */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !previewing) return;
    let raf = 0;
    const watch = () => {
      if (video.currentTime >= start + segmentLength) {
        video.pause();
        video.currentTime = start;
        setPreviewing(false);
        return;
      }
      raf = requestAnimationFrame(watch);
    };
    raf = requestAnimationFrame(watch);
    return () => cancelAnimationFrame(raf);
  }, [previewing, start, segmentLength]);

  const startFromClientX = useCallback(
    (clientX: number, offset: number) => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const ratio = (clientX - rect.left - offset) / rect.width;
      setStart(clamp(ratio * duration, 0, maxStart));
    },
    [duration, maxStart],
  );

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const windowLeftPx = (start / duration) * rect.width;
    const pointerX = e.clientX - rect.left;
    const windowWidthPx = (segmentLength / duration) * rect.width;

    // Grabbing inside the window drags it; tapping outside jumps it, centred.
    const inside = pointerX >= windowLeftPx && pointerX <= windowLeftPx + windowWidthPx;
    grabOffset.current = inside ? pointerX - windowLeftPx : windowWidthPx / 2;

    track.setPointerCapture(e.pointerId);
    setDragging(true);
    startFromClientX(e.clientX, grabOffset.current);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragging) return;
    startFromClientX(e.clientX, grabOffset.current);
  }

  function endDrag(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragging) return;
    trackRef.current?.releasePointerCapture(e.pointerId);
    setDragging(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const step = e.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
    const moves: Record<string, number> = {
      ArrowLeft: -step,
      ArrowRight: step,
      PageDown: -KEY_STEP_LARGE,
      PageUp: KEY_STEP_LARGE,
    };
    if (e.key in moves) {
      e.preventDefault();
      setStart((s) => clamp(s + moves[e.key], 0, maxStart));
    } else if (e.key === "Home") {
      e.preventDefault();
      setStart(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setStart(maxStart);
    }
  }

  function togglePreview() {
    const video = videoRef.current;
    if (!video) return;
    if (previewing) {
      video.pause();
      setPreviewing(false);
      return;
    }
    video.currentTime = start;
    void video.play();
    setPreviewing(true);
  }

  async function save() {
    setSaving(true);
    try {
      await setSegment(auth, clip.clipId, start);
      onToast("Segment locked in.", "success");
      onDone();
    } catch (err) {
      onToast(errorText(err, "Couldn't save that segment."), "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bd-screen">
      <NavBar
        subnav={
          <>
            <span>Pick your segment</span>
            <span aria-hidden="true">&middot;</span>
            <span>{segmentLength}s</span>
            <span aria-hidden="true">&middot;</span>
            <span>
              {chunks.length} &times; {SUBCHUNK_SEC}s
            </span>
          </>
        }
        actions={<NavWord onClick={onDone}>Back</NavWord>}
      />

      <main className="shell bd-stack-lg">
        <Panel title="Your clip" aside={<span className="t-micro">{formatTime(duration)}</span>}>
          <div className="bd-stage">
            <video
              ref={videoRef}
              className="bd-stage-media"
              src={clip.url}
              playsInline
              muted={false}
              preload="metadata"
            />
          </div>

          <div className="cluster bd-stage-controls">
            <Button variant="primary" onClick={togglePreview}>
              {previewing ? "Stop" : "Preview segment"}
            </Button>
            <span className="t-label grow center" aria-live="polite">
              {formatTime(start)} &rarr; {formatTime(start + segmentLength)}
            </span>
          </div>

          {/* Fixed-width window over the whole clip. Pointer events cover
              mouse, touch and pen with one code path. */}
          <div
            ref={trackRef}
            className="bd-timeline"
            data-dragging={dragging}
            role="slider"
            tabIndex={0}
            aria-label="Segment start"
            aria-valuemin={0}
            aria-valuemax={Math.round(maxStart)}
            aria-valuenow={Math.round(start)}
            aria-valuetext={`Starts at ${formatTime(start)}, ends at ${formatTime(start + segmentLength)}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onKeyDown={onKeyDown}
          >
            <div
              className="bd-timeline-window"
              style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
            >
              <span className="bd-timeline-grip is-start" aria-hidden="true" />
              {chunks.map((chunk) => (
                <span key={chunk.index} className="bd-timeline-sub" aria-hidden="true">
                  {chunk.index + 1}
                </span>
              ))}
              <span className="bd-timeline-grip is-end" aria-hidden="true" />
            </div>
          </div>

          <div className="bd-timeline-ruler t-micro muted" aria-hidden="true">
            <span>0:00</span>
            <span>{formatTime(duration / 2)}</span>
            <span>{formatTime(duration)}</span>
          </div>

          <p className="t-micro muted">
            Drag the orange window. The dashed lines are the {SUBCHUNK_SEC}-second
            sub-chunks you&rsquo;ll record one at a time. Arrow keys nudge, Shift for
            bigger jumps.
          </p>

          <hr className="dotted-divider" />

          <Button variant="submit" block beacon disabled={saving} onClick={() => void save()}>
            {saving ? "Saving…" : "Use this segment"}
          </Button>
        </Panel>
      </main>

      <Footer />
    </div>
  );
}
